import { OwnedPlanet, OwnedPlanets, Resource, Sheet } from '@prometheus/types'
import { isPlanetHoldingForUpgrade } from '@prometheus/utils'

// The forecast answers "what do the next N years cost me, and what do my mines
// bring in over the same window?". A planet spends resources (levelling up) and
// produces resources (mining) on the SAME cadence: both happen when the planet
// completes a "cycle" — a planet-token roll, or `tile` years of sub-levels when
// planet tokens are disabled (see TurnService.mineResourceCollection /
// evolvePlanetsWithNoResourceRequirements on the server).
//
// The one place the clocks separate is a HOLDING planet — one sitting on a
// completed cycle it could not pay for. Its progress bar freezes at a full
// cycle while its mines pay on `upgrade_hold_turns` instead, and the payout for
// the banked cycle already happened on the turn the cycle completed. Reading
// the frozen bar as future production promises the player a mine payout that
// never arrives — a "surplus" on exactly the planets that are starving.

export const DEFAULT_FORECAST_YEARS = 3
// Ceiling for the year picker; also the fallback when the game's deed-level
// count is unknown (planet_generation_options.number_of_levels defaults to 10).
export const MAX_FORECAST_YEARS = 10

export interface ForecastOptions {
  /** How many years ahead to forecast. Defaults to DEFAULT_FORECAST_YEARS. */
  years?: number
  /** From game options — when false, planets level on dice rolls instead of tiles. */
  disable_planet_tokens?: boolean
  /** From game options — how many planet tokens are rolled each year. */
  planet_rolls_count?: number
  /** sheet.recession_mine_production_modifier */
  recession_modifier?: number
  /** calcQuest(sheet.quests.rewards.mine_production_multiplier) */
  quest_production_bonus?: number
}

const zeroed = (): Resource & { [key: string]: number } => ({
  Wa: 0,
  Cu: 0,
  Fe: 0,
  Ag: 0,
  Au: 0,
  Oil: 0,
  Dia: 0,
  Ti: 0,
  Pt: 0,
  U: 0,
})

/** Probability of rolling `tile` on two six-sided dice. */
export const diceProbability = (tile: number) => {
  if (!Number.isFinite(tile) || tile < 2 || tile > 12) {
    return 0
  }
  return (6 - Math.abs(7 - tile)) / 36
}

/**
 * How many production / level-up cycles a planet completes in `years`.
 * Whole cycles when planet tokens are disabled (the default game mode);
 * an expected — and therefore fractional — count when they are rolled for.
 */
export const getForecastCycles = (planet: OwnedPlanet, years: number, options: ForecastOptions) => {
  if (!(years > 0)) {
    return 0
  }

  if (options.disable_planet_tokens === false) {
    // Planet tokens: the tile is a dice number (2-12) and the planet cycles
    // whenever it comes up in the year's rolls.
    const rolls = Math.max(0, options.planet_rolls_count || 0)
    const chance_per_year = 1 - Math.pow(1 - diceProbability(Math.round(planet.tile)), rolls)
    return years * chance_per_year
  }

  // No planet tokens: the tile is how many years a cycle takes and is
  // FRACTIONAL (2.5 … 1.5 by default), so it must not be rounded. Sub-levels
  // tick up a year at a time and a cycle spends `tile` of them; the epsilon
  // keeps float dust from swallowing an exact boundary.
  const tile = planet.tile > 0 ? planet.tile : 1
  return Math.floor(((planet.sub_levels_current || 0) + years) / tile + 1e-9)
}

// Holds only exist in tile-driven (token-disabled) games — dice-mode planets
// never bank a cycle they can't pay for.
const isHolding = (planet: OwnedPlanet, options: ForecastOptions) =>
  options.disable_planet_tokens !== false && isPlanetHoldingForUpgrade(planet)

/**
 * Level-up attempts inside the window. Same as `getForecastCycles` for a
 * growing planet; a HOLDING planet retries its banked level immediately (its
 * cost is a live demand, so it counts now) and — assuming the player stocks
 * up, which is what the forecast is for — growth resumes a cycle behind, from
 * `sub - tile`, rather than from the frozen bar.
 */
export const getForecastUpgradeCycles = (planet: OwnedPlanet, years: number, options: ForecastOptions) => {
  if (!(years > 0) || !isHolding(planet, options)) {
    return getForecastCycles(planet, years, options)
  }
  const tile = planet.tile > 0 ? planet.tile : 1
  const resumed_progress = Math.max(0, (planet.sub_levels_current || 0) - tile)
  return 1 + Math.floor((resumed_progress + years - 1) / tile + 1e-9)
}

/**
 * Mine payouts inside the window. A HOLDING planet already collected for the
 * banked cycle on the turn it completed; while it holds, the engine pays its
 * mines off `upgrade_hold_turns` — a payout on each turn the counter crosses
 * a whole `tile` (see TurnService.mineResourceCollection / yearsUntilNextYield)
 * — so the frozen progress bar must NOT be counted as another payout.
 */
export const getForecastProductionCycles = (planet: OwnedPlanet, years: number, options: ForecastOptions) => {
  if (!(years > 0) || !isHolding(planet, options)) {
    return getForecastCycles(planet, years, options)
  }
  const tile = planet.tile > 0 ? planet.tile : 1
  const held = Math.max(1, planet.upgrade_hold_turns || 0)
  // Payout in year k when `held + k - 1` crosses a cycle; summed over the
  // window the crossings telescope to the difference of the two floors.
  return Math.floor((held - 1 + years) / tile + 1e-9) - Math.floor((held - 1) / tile + 1e-9)
}

/** The highest level index this planet may still reach (level lock aware). */
const maxReachableLevel = (planet: OwnedPlanet) => {
  const last_level = (planet.deed?.levels?.length || 0) - 1
  if (planet.levelLock === undefined || planet.levelLock === -1) {
    return last_level
  }
  return Math.min(last_level, planet.levelLock - 1)
}

/** Years the picker offers, given the game's configured deed-level count. */
export const getForecastYearOptions = (number_of_levels?: number) => {
  const levels = Number.isFinite(number_of_levels as number)
    ? Math.floor(number_of_levels as number)
    : MAX_FORECAST_YEARS
  const max = Math.min(MAX_FORECAST_YEARS, Math.max(DEFAULT_FORECAST_YEARS, levels))
  return Array.from({ length: max }, (_, i) => i + 1)
}

/** Resources the player finishes the forecast window short of. */
export const getResourceShortages = (net_forecast?: Resource & { [key: string]: number }) => {
  if (!net_forecast) {
    return []
  }
  return Object.keys(net_forecast).filter(r => net_forecast[r] < 0)
}

export const getResourceUsageForecast = (
  planets: OwnedPlanets,
  sheet_resources: Sheet['resources'] | undefined,
  cash: number | undefined,
  options: ForecastOptions = {},
) => {
  const years = Math.max(0, options.years ?? DEFAULT_FORECAST_YEARS)
  const production_modifier = (options.recession_modifier ?? 1) * (1 + (options.quest_production_bonus ?? 0))

  const _current_mines = zeroed()
  const _current_barriers = zeroed()
  const _maximum_mines = zeroed()
  const _usage_forecast = zeroed()
  const _production_forecast = zeroed()

  for (const [planet] of Object.entries(planets)) {
    const owned = planets[planet]
    const upgrade_cycles = getForecastUpgradeCycles(owned, years, options)
    const production_cycles = getForecastProductionCycles(owned, years, options)

    owned.mines.forEach((mine) => {
      const mineKey = Object.keys(mine)[0]
      _current_mines[mineKey] += mine[mineKey]
      _maximum_mines[mineKey] += owned.deed.mine_production_maximum[mineKey]
      _production_forecast[mineKey] += mine[mineKey] * production_cycles * production_modifier
    })
    let count = 0
    owned.deed.disasterBarriers.forEach((barrier) => {
      _current_barriers[barrier] += owned.deed.disasterBarrierCountCurrent[count]
      count += 1
    })

    // Levels the planet actually gains inside the window — capped by the end of
    // the deed's ladder and by any level lock, exactly like the server does.
    const levels_gained = Math.min(Math.floor(upgrade_cycles), Math.max(0, maxReachableLevel(owned) - owned.level))
    owned.deed.levels.forEach((level, i) => {
      if (i > owned.level && i - owned.level <= levels_gained) {
        for (const [resource, amount] of Object.entries(level.cost.resources || {})) {
          _usage_forecast[resource] += amount
        }
      }
    })
  }

  const _current_resources = zeroed()
  for (const resource of Object.keys(_current_resources)) {
    _current_resources[resource] = sheet_resources?.[resource] || 0
  }

  const _net_forecast = zeroed()
  for (const resource of Object.keys(_net_forecast)) {
    _production_forecast[resource] = Math.round(_production_forecast[resource])
    _net_forecast[resource] = _current_resources[resource] + _production_forecast[resource] - _usage_forecast[resource]
  }

  const _cash = (cash || 0)
  return {
    years,
    maximum_mines: _maximum_mines,
    usage_forecast: _usage_forecast,
    production_forecast: _production_forecast,
    net_forecast: _net_forecast,
    current_mines: _current_mines,
    sheet_resources2: _current_resources,
    current_barriers: _current_barriers,
    cash2: _cash,
  }
}
